import { supabase } from './supabase';

export type Profile = {
  id: string;
  display_name: string | null;
  bio: string | null;
  photo_url: string | null;
  verified: boolean;
  created_at: string;
  email: string | null;
  phone: string | null;
  name: string | null;
};

export type UserRole = 'admin' | 'landlord' | 'user';

export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    return { error };
  } catch (error) {
    console.error('Error signing out:', error);
    return { error };
  }
}

export async function getProfile(userId: string) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    return { data, error };
  } catch (error) {
    console.error('Error getting profile:', error);
    return { data: null, error };
  }
}

export async function updateProfile(userId: string, updates: Partial<Profile>) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    
    return { data, error };
  } catch (error) {
    console.error('Error updating profile:', error);
    return { data: null, error };
  }
}

export async function getUserRole(): Promise<UserRole> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 'user';
    
    return (user.user_metadata?.role as UserRole) || 'user';
  } catch (error) {
    console.error('Error getting user role:', error);
    return 'user';
  }
}

export async function isAdmin(): Promise<boolean> {
  try {
    const role = await getUserRole();
    return role === 'admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

export async function isLandlord(): Promise<boolean> {
  try {
    const role = await getUserRole();
    return role === 'landlord';
  } catch (error) {
    console.error('Error checking landlord status:', error);
    return false;
  }
}

export function isDemoAccount(email: string | null | undefined): boolean {
  if (!email) return false;
  return email === 'admin@example.com' || 
         email === 'user@example.com' || 
         email === 'landlord@example.com';
}

// For testing purposes - bypass authentication
export function bypassAuth() {
  // This function simulates a successful authentication
  // It's only for development/testing and should be removed in production
  return {
    user: {
      id: 'test-user-id',
      email: 'test@example.com',
      user_metadata: {
        role: 'admin'
      }
    },
    session: {
      access_token: 'fake-token',
      refresh_token: 'fake-refresh-token',
      expires_at: Date.now() + 3600000 // 1 hour from now
    }
  };
}

export async function resetPassword(email: string) {
  try {
    // Generate the reset link
    const resetLink = `${window.location.origin}/reset-password`;
    
    // Send the reset password request to Supabase
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: resetLink,
    });
    
    if (error) throw error;
    
    // Optionally, send a custom email with better instructions
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/password-reset-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            email,
            resetLink
          }),
        });
      }
    } catch (emailError) {
      console.error('Error sending custom email:', emailError);
      // Continue even if custom email fails, as Supabase will send a default email
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error resetting password:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to send password reset email'
    };
  }
}

export async function updateUserPassword(password: string) {
  try {
    const { error } = await supabase.auth.updateUser({ password });
    
    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    console.error('Error updating password:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to update password'
    };
  }
}

// New function to sign in with email and password
export async function signInWithEmail(email: string, password: string, rememberMe: boolean = false) {
  try {
    // Trim inputs to prevent whitespace issues
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email: trimmedEmail,
      password: trimmedPassword,
      options: {
        persistSession: rememberMe
      }
    });

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        return { 
          data: null, 
          error: 'The password you entered is incorrect. Please try again or use the "Forgot password" link to reset it.'
        };
      }
      throw error;
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error signing in with email:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Failed to sign in'
    };
  }
}

// New function to sign up with email and password
export async function signUpWithEmail(email: string, password: string) {
  try {
    // Trim inputs to prevent whitespace issues
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    
    const { data, error } = await supabase.auth.signUp({
      email: trimmedEmail,
      password: trimmedPassword,
      options: {
        data: {
          email_confirmed_at: new Date().toISOString(),
          role: 'user'
        }
      }
    });

    if (error) {
      if (error.message.includes('User already registered')) {
        return { 
          data: null, 
          error: 'An account with this email already exists. Please sign in instead.'
        };
      }
      throw error;
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error signing up with email:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Failed to sign up'
    };
  }
}

// New function to refresh session
export async function refreshSession() {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error('Error refreshing session:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Failed to refresh session'
    };
  }
}